import {useState, useEffect, useMemo} from 'react';
import {TextField, Button, CircularProgress, Typography, Box, Autocomplete, MenuItem, Select, FormControl, InputLabel} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import icon from '../../../public/favicon.svg'
import { locationsByState } from '../../data/schoolLocations';
import SmsConsentCheckbox from './SmsConsentCheckbox';

interface ContactFormProps {
    onSchoolChange?: (schoolKey: string | null) => void;
}

function ContactForm({ onSchoolChange }: ContactFormProps) {
    const [searchParams] = useSearchParams();
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [complete, setComplete] = useState(false);

    const [formData, setFormData] = useState({
        school: '',
        name: '',
        age: '',
        email: '',
        phoneNumber: '',
        message: '',
        origin: '',
        hearAbout: '',
        // New fields
        childFirstName: '',
        childLastName: '',
        birthday: '',
        parentFirstName: '',
        parentLastName: '',
        gradeInterest: '',
    });

    const [birthdayValue, setBirthdayValue] = useState<Dayjs | null>(null);
    const [availableGrades, setAvailableGrades] = useState<string[]>([]);

    // Set school from URL params on mount and clean up URL
    useEffect(() => {
        const schoolParam = searchParams.get('school');
        if (schoolParam) {
            setFormData(prev => ({ ...prev, school: schoolParam }));
            onSchoolChange?.(schoolParam);
            // Remove the school parameter from the URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('school');
            const newUrl = newSearchParams.toString()
                ? `${window.location.pathname}?${newSearchParams.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }, [searchParams, onSchoolChange]);

    const allLocations = useMemo(() =>
        Object.entries(locationsByState).flatMap(([, locations]) =>
            locations.map((loc) => {
                const trimmedAddress2 = loc.address2.slice(0, -6); // Remove space + 5-digit ZIP
                return {
                    label: `${loc.name} - ${trimmedAddress2}`,
                    value: loc.schoolKey,
                    grades: loc.grades || []
                };
            })
        )
    , []);

    // Update available grades when school changes
    useEffect(() => {
        if (formData.school) {
            const selectedSchool = allLocations.find(loc => loc.value === formData.school);
            if (selectedSchool && selectedSchool.grades) {
                setAvailableGrades(selectedSchool.grades);
            } else {
                setAvailableGrades([]);
            }
        } else {
            setAvailableGrades([]);
        }
    }, [formData.school, allLocations]);

    // Calculate age from birthday
    const calculateAge = (birthdayString: string) => {
        if (!birthdayString) return '';
        const birthday = new Date(birthdayString);
        const today = new Date();
        const years = today.getFullYear() - birthday.getFullYear();
        const months = today.getMonth() - birthday.getMonth();

        let ageYears = years;
        let ageMonths = months;

        if (months < 0) {
            ageYears--;
            ageMonths = 12 + months;
        }

        return `${ageYears} years ${ageMonths} months`;
    };

    const handleChange = (event: { target: { name: string; value: string; }; }) => {
        const {name, value} = event.target;
        const updatedFormData = {...formData, [name]: value};

        // If child first or last name changes, combine them into name field
        if (name === 'childFirstName' || name === 'childLastName') {
            const firstName = name === 'childFirstName' ? value : formData.childFirstName;
            const lastName = name === 'childLastName' ? value : formData.childLastName;
            updatedFormData.name = `${firstName} ${lastName}`.trim();
        }

        // If birthday changes, calculate age
        if (name === 'birthday') {
            updatedFormData.age = calculateAge(value);
        }

        setFormData(updatedFormData);
    };

    const handleSubmit = async (event: { preventDefault: () => void; }) => {
        event.preventDefault();
        setFormSubmitting(true);
        try {
            // Prepare submission data with grade interest appended to message
            const submissionData = {
                ...formData,
                origin: localStorage.getItem('o') ?? '',
                canText: true,
                message: formData.gradeInterest
                    ? `Interested Grade: ${formData.gradeInterest}\\n\\n${formData.message}`
                    : formData.message
            };
            console.log('Submitting form data:', submissionData); // Debug log to verify gradeInterest is included
            const response = await fetch('https://ccq7lcnxgdh6lek7v6kv7364gu0dleen.lambda-url.us-east-1.on.aws/', {
                method: 'POST',
                body: JSON.stringify(submissionData)
            });
            if (response.ok) {
                setComplete(true);
            } else {
                setErrorMessage('There has been an error while attempting to submit this form. Please try again later.');
            }

            setFormSubmitting(false);
        } catch (error) {
            setErrorMessage('An error has occurred while attempting to submit this form. Please try again later.');
            setFormSubmitting(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        {complete ?
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <Box component={"img"} src={icon} sx={{height: 100, filter: 'brightness(0%)'}}/>
                <br/>
                <Typography
                    variant={"h4"} sx={{fontWeight: 'bold',}}>Thank you for reaching out!<p/>We will be in contact
                    soon.</Typography>
            </Box> :
            <form onSubmit={handleSubmit}>
                <Autocomplete
                    fullWidth
                    options={allLocations}
                    getOptionLabel={(option) => option.label}
                    value={
                        formData.school
                            ? allLocations.find(option => option.value === formData.school) ?? null
                            : null
                    }
                    onChange={(_, newValue) => {
                        const schoolKey = newValue?.value ?? '';
                        setFormData({ ...formData, school: schoolKey });
                        onSchoolChange?.(schoolKey || null);
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select School"
                            required
                            sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        />
                    )}
                />
                {/* Child First and Last Name - side by side on desktop, stacked on mobile */}
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, mt: 3 }}>
                    <TextField
                        name="childFirstName"
                        label="Child First Name"
                        value={formData.childFirstName}
                        onChange={handleChange}
                        required
                        fullWidth
                        sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                    />
                    <TextField
                        name="childLastName"
                        label="Child Last Name"
                        value={formData.childLastName}
                        onChange={handleChange}
                        required
                        fullWidth
                        sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                    />
                </Box>

                {/* Birthday - Material Date Picker */}
                <DatePicker
                    label="Child Birthday"
                    value={birthdayValue}
                    onChange={(newValue) => {
                        setBirthdayValue(newValue);
                        if (newValue) {
                            const birthdayString = newValue.format('YYYY-MM-DD');
                            const updatedFormData = {
                                ...formData,
                                birthday: birthdayString,
                                age: calculateAge(birthdayString)
                            };
                            setFormData(updatedFormData);
                        }
                    }}
                    slotProps={{
                        textField: {
                            required: true,
                            fullWidth: true,
                            margin: 'normal',
                            sx: { mt: 3, textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }
                        }
                    }}
                />

                {/* Grade Interest - Disabled until school is selected */}
                <FormControl
                    fullWidth
                    sx={{ mt: 3, textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    disabled={!formData.school || availableGrades.length === 0}
                >
                    <InputLabel id="grade-interest-label">Grade Interest</InputLabel>
                    <Select
                        labelId="grade-interest-label"
                        name="gradeInterest"
                        value={formData.gradeInterest}
                        onChange={(e) => {
                            const updatedFormData = {
                                ...formData,
                                gradeInterest: e.target.value as string
                            };
                            setFormData(updatedFormData);
                            console.log('Grade Interest updated:', e.target.value); // Debug log
                        }}
                        label="Grade Interest"
                    >
                        {availableGrades.map((grade) => (
                            <MenuItem key={grade} value={grade}>
                                {grade}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Parent First and Last Name - side by side on desktop, stacked on mobile */}
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, mt: 3 }}>
                    <TextField
                        name="parentFirstName"
                        label="Parent First Name"
                        value={formData.parentFirstName}
                        onChange={handleChange}
                        fullWidth
                        sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                    />
                    <TextField
                        name="parentLastName"
                        label="Parent Last Name"
                        value={formData.parentLastName}
                        onChange={handleChange}
                        fullWidth
                        sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                    />
                </Box>

                {/* Parent Phone - Required */}
                <TextField
                    name="phoneNumber"
                    label="Parent Phone Number"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    fullWidth
                    margin="normal"
                    sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                />

                {/* Parent Email - Optional */}
                <TextField
                    name="email"
                    label="Parent Email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                />

                {/* Message - Optional */}
                <TextField
                    name="message"
                    label="Message"
                    value={formData.message}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                    sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                />
                <TextField
                    name="hearAbout"
                    label="How did you hear about us?"
                    value={formData.hearAbout}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    sx={{textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
                />
                <SmsConsentCheckbox />
                <Typography sx={{color: 'red',}}>{errorMessage}</Typography>
                <Button type="submit" variant="contained" color="primary" disabled={formSubmitting}>
                    {formSubmitting ? <CircularProgress/> : "Submit"}
                </Button>
            </form>
        }
        </LocalizationProvider>
    );
}

export default ContactForm;
